/**
 * Category Normalizer - Normalise et verrouille l'ordre des catégories
 * Élimine l'erreur d'hydratation #418 en assurant le même tri SSR et client
 */

export interface NormalizedCategory {
  slug: string
  name_fr: string
  name_es: string
  show_in_nav: boolean
  order: number
  normalized_slug: string // Slug normalisé pour la cohérence
}

/**
 * Normalise un slug de catégorie pour assurer la cohérence
 */
export function normalizeCategorySlug(slug: string): string {
  return slug
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '-') // Remplacer les caractères spéciaux par des tirets
    .replace(/-+/g, '-') // Remplacer les tirets multiples par un seul
    .replace(/^-|-$/g, '') // Supprimer les tirets en début/fin
}

/**
 * Ordre fixe des catégories pour assurer la cohérence SSR/CSR
 */
const CATEGORY_ORDER: Record<string, number> = {
  'rep-dom': 1,
  'canada': 2,
  'monde': 3,
  'mundo': 3,
  'economie': 4,
  'politique-rd': 5,
  'politica-rd': 5,
  'environnement': 6,
  'sciences-tech': 7,
  'entrevista': 8,
  'opinion': 9,
  'voces-en-conflicto': 10
}

/**
 * Normalise et trie les catégories de manière déterministe
 */
export function normalizeCategories(categories: any[]): NormalizedCategory[] {
  return categories
    .map(category => ({
      ...category,
      normalized_slug: normalizeCategorySlug(category.slug)
    }))
    .sort((a, b) => {
      // 1. D'abord par ordre défini
      const orderA = CATEGORY_ORDER[a.normalized_slug] || 999
      const orderB = CATEGORY_ORDER[b.normalized_slug] || 999
      
      if (orderA !== orderB) {
        return orderA - orderB
      }
      
      // 2. Ensuite par order field
      if (a.order !== b.order) {
        return a.order - b.order
      }
      
      // 3. Enfin par slug alphabétique pour la stabilité
      return a.normalized_slug.localeCompare(b.normalized_slug)
    })
    .filter(category => category.show_in_nav) // Filtrer seulement les catégories visibles
}

/**
 * Vérifie la cohérence des catégories entre SSR et CSR
 */
export function validateCategoryConsistency(
  ssrCategories: NormalizedCategory[],
  csrCategories: NormalizedCategory[]
): boolean {
  if (ssrCategories.length !== csrCategories.length) {
    console.error('❌ Nombre de catégories différent entre SSR et CSR:', {
      ssr: ssrCategories.length,
      csr: csrCategories.length
    })
    return false
  }

  for (let i = 0; i < ssrCategories.length; i++) {
    const ssr = ssrCategories[i]
    const csr = csrCategories[i]
    
    if (ssr.normalized_slug !== csr.normalized_slug) {
      console.error('❌ Ordre des catégories différent entre SSR et CSR:', {
        index: i,
        ssr: ssr.normalized_slug,
        csr: csr.normalized_slug
      })
      return false
    }
  }

  console.log('✅ Cohérence des catégories validée')
  return true
}

/**
 * Hook pour utiliser les catégories normalisées côté client
 */
export function useNormalizedCategories(categories: any[]) {
  const normalized = normalizeCategories(categories)
  
  // Validation en développement
  if (process.env.NODE_ENV === 'development') {
    console.log('📋 Catégories normalisées:', normalized.map(c => c.normalized_slug))
  }
  
  return normalized
}
